import { DateUtils, CONS } from '@pomofocus/core';
const sumArray = (arr) => {
    return arr.reduce((a, b) => a + b, 0);
};
/**
 * Converts a date object to MMM-YYYY format
 */
export function convertDateToMMYY(dateObj) {
    const y = dateObj.getFullYear();
    const m = dateObj.getMonth();
    const mStr = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ][m];
    return `${mStr}-${y}`;
}
/**
 * Generates an array of date strings for the specified number of days
 */
export function generateDaysString(daysBefore, backCnt, lastDate) {
    lastDate.setDate(lastDate.getDate() - daysBefore * backCnt);
    const dateStrings = [];
    for (let i = 0; i < daysBefore; i++) {
        const { dateStr } = DateUtils.convertDateToString(lastDate);
        dateStrings.unshift(dateStr);
        lastDate.setDate(lastDate.getDate() - 1);
    }
    return dateStrings;
}
/**
 * Generates an array of month strings for the specified year
 */
export function generateMonthsString(thisYear, backCnt) {
    const reportYear = thisYear - backCnt;
    const monthsString = DateUtils.MONTHS.map((m) => `${m}-${reportYear}`);
    return monthsString;
}
/**
 * Generates chart dataset from daily work minutes data
 */
export function generateDataset(dayStrings, dailyWorkMinutesPre) {
    const dataset = [];
    const projects = [
        ...new Set(dailyWorkMinutesPre.map((d) => d.projectName)),
    ];
    projects.forEach((label) => {
        const data = [];
        dayStrings.forEach((dateStr) => {
            const thisDailyWorkMinutes = dailyWorkMinutesPre.filter((m) => m.dateStr === dateStr && m.projectName === label);
            if (thisDailyWorkMinutes.length > 0) {
                const minutesArr = thisDailyWorkMinutes.map((thisDailyWorkMinute) => thisDailyWorkMinute.minutes);
                const totalMinutes = minutesArr.reduce((a, b) => {
                    return a + b;
                });
                data.push(totalMinutes / 60);
            }
            else {
                data.push(0);
            }
        });
        dataset.push({
            label: label || 'No Project',
            data,
            dataSum: sumArray(data),
        });
    });
    const filteredDatasets = dataset.filter((d) => {
        if (d.label !== 'No Project')
            return true;
        if (Math.max(...d.data) > 0)
            return true;
        return false;
    });
    filteredDatasets.sort((a, b) => b.dataSum - a.dataSum);
    return filteredDatasets;
}
/**
 * Generates a list of date digits for the specified number of days back
 */
export function generateDateList(dateObj, daysBack) {
    const dateDigitList = [];
    for (let i = 0; i < daysBack; i++) {
        const { dateDigit } = DateUtils.convertDateToString(dateObj);
        dateDigitList.push(Number(dateDigit));
        dateObj.setDate(dateObj.getDate() - 1);
    }
    return dateDigitList;
}
/**
 * Converts the "Start of the week" setting to a day index
 * (0: Sunday, 1: Monday, 6: Saturday). Defaults to Monday.
 */
export const getWeekStartDay = (weekStart) => {
    if (weekStart === CONS.WEEK_START_SUNDAY)
        return 0;
    if (weekStart === CONS.WEEK_START_SATURDAY)
        return 6;
    return 1;
};
/**
 * Gets the end of the week for a given date
 * (Sunday when the week starts on Monday)
 */
export const getEndOfTheWeek = (today, weekStartDay = 1) => {
    const daysLeft = (weekStartDay + 6 - today.getDay()) % 7;
    return new Date(today.setDate(today.getDate() + daysLeft));
};
/**
 * Gets the start of the week (Monday by default) for a given date
 */
export const getStartOfTheWeek = (today, weekStartDay = 1) => {
    const daysPassed = (today.getDay() - weekStartDay + 7) % 7;
    return new Date(today.setDate(today.getDate() - daysPassed));
};
export const getLastDateOfTheWeek = (today) => {
    const day = today.getDay();
    if (day === 0)
        return new Date(today);
    return new Date(today.setDate(today.getDate() + (7 - day)));
};
/**
 * Changes date labels in daily work minutes data
 */
export const changeLabel = (dailyWorkMinutes) => {
    try {
        const dailyWorkHours = dailyWorkMinutes.map((d) => {
            const { dateStr } = DateUtils.convertDateToString(DateUtils.convertDateStrToDateObj(d.dateStr));
            return {
                ...d,
                dateStr,
            };
        });
        return dailyWorkHours;
    }
    catch (err) {
        return dailyWorkMinutes;
    }
};
/**
 * Converts daily work data to monthly format
 */
export function changeFromDateToMongh(dailyWorkMinutes) {
    const monthlyWorkHours = dailyWorkMinutes.map((d) => {
        const monthString = convertDateToMMYY(DateUtils.convertDateStrToDateObj(d.dateStr));
        return {
            ...d,
            dateStr: monthString,
        };
    });
    return monthlyWorkHours;
}
/**
 * Converts daily work data to weekly format
 */
export const changeLabelFromDateToWeek = (dailyWorkMinutes, weekStartDay = 1) => {
    const monthlyWorkHours = dailyWorkMinutes.map((d) => {
        const startOfTheWeek = getStartOfTheWeek(DateUtils.convertDateStrToDateObj(d.dateStr), weekStartDay);
        const { dateStr } = DateUtils.convertDateToString(startOfTheWeek);
        return {
            ...d,
            dateStr,
        };
    });
    return monthlyWorkHours;
};
/**
 * Generates an array of week strings
 */
export function generateWeeksString(endOfTheWeek, weeksBefore, backCnt) {
    const latestWeek = new Date(endOfTheWeek);
    latestWeek.setDate(latestWeek.getDate() - 6);
    latestWeek.setDate(latestWeek.getDate() - weeksBefore * 7 * backCnt);
    const weekStrings = [];
    for (let i = 0; i < weeksBefore; i++) {
        const { dateStr } = DateUtils.convertDateToString(latestWeek);
        weekStrings.unshift(dateStr);
        latestWeek.setDate(latestWeek.getDate() - 7);
    }
    return weekStrings;
}
